// GTM dataLayer tracking for the "Pick up where you left off" (Last Visited PDP) widget —
// the "Drop Off banner" CRO experiment (mweb).
//
// The widget is not theme markup — it is injected at runtime (no Liquid source in this repo),
// so this file auto-wires itself to its DOM the same way pair-popup-tracking.js does for the
// Convert popup: it watches for [data-dk-lvp] being inserted and tracks from there.
//
// Events, per the "E-commerce for Drop Off banner test" tracking spec:
//   view_item_list  widget is at least half on screen with its product filled in. The same
//                   product is only impressed once per page load; a fresh page load resets that.
//   select_item     click on the product (image, name, variant, price) — not the ADD TO CART
//                   button, the ✕, or the "Pick up where you left off" header.
//   add_to_cart     only after a /cart/add request (fetch or XHR) made within ATC_WINDOW_MS of
//                   an ADD TO CART click has succeeded AND returned the widget's product.
//
// RESOLVING THE PRODUCT
// The widget markup carries only a name, a variant label, prices and an image — no ids. So the
// product is looked up as soon as the widget appears (well before the user can click it):
//   1. data-variant-id / data-product-id / data-product-handle on [data-dk-lvp], if present;
//   2. otherwise the handles in localStorage "themex-recently-viewed" (what "you were viewing
//      earlier" is built from), then Shopify predictive search on the product name;
// each candidate's /products/<handle>.js is matched on the image file name or the title, and
// the variant on its title/option ("60ml"), its image, or its price. That gives item_id (the
// variant SKU per the CRO spec, the variant id when there is no SKU), item_name and item_category.
// add_to_cart takes its ids from the /cart/add response itself.
//
// exp_variant_string: window.dnkExperimentVariant (the theme-wide hook) wins, then
// data-exp-variant on the widget, then the Convert experience/variation the visitor is bucketed
// into ("<experience name> | <variation name>"), then null.
(() => {
  const WIDGET_SELECTOR      = '[data-dk-lvp]';
  const PRODUCT_CLICK_SELECTOR = '.dk-lvp-img, .dk-lvp-name';
  const CTA_SELECTOR         = '.dk-lvp-cta';
  // dataLayer event names — must equal the "Event name" of the CE - LVP CRO - * triggers in GTM.
  const EVENTS = {
    view:      'view_item_list',
    select:    'select_item',
    addToCart: 'add_to_cart',
  };
  const LIST_ID              = 'drop_off_banner';
  const LIST_NAME            = 'Drop Off banner';
  const BRAND                = 'Dot & Key';
  const RECENTLY_VIEWED_KEY  = 'themex-recently-viewed';
  const CART_ADD_RE          = /\/cart\/add(\.js)?(\?|$)/;
  // How long after an ADD TO CART click a /cart/add request is attributed to this widget.
  const ATC_WINDOW_MS        = 10000;
  // Longest an impression waits on the product lookup before pushing with what it has.
  const RESOLVE_TIMEOUT_MS   = 3000;
  const MAX_CANDIDATES       = 10;

  // ── Shared utilities (window.DnkTracking) ───────────────────────────────────
  const T = (window.DnkTracking = window.DnkTracking || {});

  if (!T.getClientId) {
    const CLIENT_ID_STORAGE_KEY = 'themex-client-id';
    T.getClientId = () => {
      const gaCookie = document.cookie.match(/(?:^|;\s*)_ga=([^;]+)/);
      if (gaCookie) {
        const parts = decodeURIComponent(gaCookie[1]).split('.');
        if (parts.length >= 4) return `${parts[2]}.${parts[3]}`;
      }
      let clientId = localStorage.getItem(CLIENT_ID_STORAGE_KEY);
      if (!clientId) {
        clientId = crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        localStorage.setItem(CLIENT_ID_STORAGE_KEY, clientId);
      }
      return clientId;
    };
  }

  if (!T.getCurrency) {
    T.getCurrency = () =>
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'INR';
  }

  if (!T.toMajorUnits) {
    T.toMajorUnits = (paise) => Math.round(Number(paise || 0)) / 100;
  }

  const { getCurrency, toMajorUnits } = T;

  // A malformed _ga cookie or blocked localStorage must not stop the push.
  const getClientId = () => {
    try {
      return T.getClientId();
    } catch (_) {
      return null;
    }
  };

  const getConvertVariant = () => {
    try {
      const convert     = window.convert;
      const experiences = convert && convert.currentData && convert.currentData.experiences;
      if (!experiences) return null;

      const definitions = (convert.data && convert.data.experiences) || [];
      const values = Object.keys(experiences)
        .map((experienceId) => {
          const variation = experiences[experienceId] && experiences[experienceId].variation;
          if (!variation) return null;
          const definition = Array.isArray(definitions)
            ? definitions.find((experience) => String(experience.id) === String(experienceId))
            : definitions[experienceId];
          const experienceName = (definition && definition.name) || experienceId;
          const variationName  = variation.name || variation.id;
          return variationName ? `${experienceName} | ${variationName}` : null;
        })
        .filter(Boolean);

      return values.length ? values.join(', ') : null;
    } catch (_) {
      return null;
    }
  };

  const getExpVariantString = (widget) =>
    window.dnkExperimentVariant ||
    (widget && widget.dataset.expVariant) ||
    getConvertVariant() ||
    null;

  // ── Reading the widget ──────────────────────────────────────────────────────
  const normalize = (text) => String(text || '').toLowerCase().replace(/[^a-z0-9]+/g, '');

  // "…/files/1_c1ab…jpg?width=120" and "//…/files/1_c1ab….jpg?v=…" → "1_c1ab…"
  const imageKey = (src) => {
    if (!src) return null;
    try {
      const file = new URL(src, window.location.origin).pathname.split('/').pop();
      return file.replace(/\.[a-z0-9]+$/i, '').replace(/_(\d+x\d*|x\d+)$/i, '').toLowerCase() || null;
    } catch (_) {
      return null;
    }
  };

  const readText = (widget, selector) =>
    widget.querySelector(selector)?.textContent.trim() || null;

  const parsePrice = (text) => {
    const match = (text || '').replace(/,/g, '').match(/\d+(\.\d+)?/);
    return match ? Number(match[0]) : null;
  };

  // The widget (Convert variation) saves the product it shows under this key:
  // { productId, title, url: '/products/<handle>', variants: [{ id, label, ... }], ... }
  const WIDGET_PRODUCT_KEY = 'dk_last_viewed_product';

  const readWidgetProduct = () => {
    try {
      const saved = JSON.parse(localStorage.getItem(WIDGET_PRODUCT_KEY));
      return saved && typeof saved === 'object' ? saved : null;
    } catch (_) {
      return null;
    }
  };

  // Multi-variant products render size pills (.dk-lvp-variant-pill) or colour swatches with a
  // .dk-lvp-shade-name line; single-variant products render a plain .dk-lvp-variant label.
  // Every pill carries data-variant-id, so the active one gives the exact variant.
  const readActiveVariant = (widget) => {
    const pill  = widget.querySelector('.dk-lvp-variant-pill.is-active');
    const label =
      readText(widget, '.dk-lvp-variant') ||
      readText(widget, '.dk-lvp-shade-name') ||
      (pill && (pill.getAttribute('aria-label') || pill.textContent || '').trim()) ||
      null;
    return { label, variantId: pill ? pill.getAttribute('data-variant-id') : null };
  };

  const readWidget = (widget) => {
    const price        = parsePrice(readText(widget, '.dk-lvp-price-current'));
    const comparePrice = parsePrice(readText(widget, '.dk-lvp-price-compare'));
    const image        = widget.querySelector('.dk-lvp-img');
    const name         = readText(widget, '.dk-lvp-name');
    const active       = readActiveVariant(widget);

    // Only trust the saved product when it is the one on screen.
    const saved     = readWidgetProduct();
    const savedSame = saved && name && normalize(saved.title) === normalize(name) ? saved : null;
    const savedVariants = (savedSame && Array.isArray(savedSame.variants)) ? savedSame.variants : [];
    const handleMatch   = savedSame && String(savedSame.url || '').match(/\/products\/([^/?#]+)/);

    return {
      name,
      variant:      active.label,
      price,
      comparePrice,
      imageKey:     imageKey(image && (image.currentSrc || image.getAttribute('src'))),
      variantId:    widget.dataset.variantId || active.variantId ||
                    (savedVariants.length === 1 && savedVariants[0].id != null ? String(savedVariants[0].id) : null),
      productId:    widget.dataset.productId || (savedSame && savedSame.productId != null ? String(savedSame.productId) : null),
      productType:  widget.dataset.productType || null,
      handle:       widget.dataset.productHandle || (handleMatch ? decodeURIComponent(handleMatch[1]) : null),
    };
  };

  // Identifies the product the widget is showing right now, so a widget whose content is
  // swapped for a different product is treated as a new product.
  const contentKey = (data) => [data.name, data.variant, data.imageKey].map(normalize).join('|');

  // ── Resolving ids from the storefront ───────────────────────────────────────
  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const productCache = new Map();

  const fetchProduct = (handle) => {
    if (!productCache.has(handle)) {
      productCache.set(
        handle,
        fetch(`${root()}products/${encodeURIComponent(handle)}.js`)
          .then((response) => (response.ok ? response.json() : null))
          .catch(() => null)
      );
    }
    return productCache.get(handle);
  };

  const getRecentlyViewedHandles = () => {
    try {
      const handles = JSON.parse(localStorage.getItem(RECENTLY_VIEWED_KEY));
      return Array.isArray(handles) ? handles.filter((h) => typeof h === 'string' && h) : [];
    } catch (_) {
      return [];
    }
  };

  const searchHandles = (name) => {
    if (!name) return Promise.resolve([]);
    const params = new URLSearchParams({
      q: name,
      'resources[type]': 'product',
      'resources[limit]': '4',
    });
    return fetch(`${root()}search/suggest.json?${params.toString()}`)
      .then((response) => (response.ok ? response.json() : null))
      .then((json) =>
        ((json && json.resources && json.resources.results && json.resources.results.products) || [])
          .map((product) => product.handle)
          .filter(Boolean)
      )
      .catch(() => []);
  };

  const productImageKeys = (product) => {
    const sources = [];
    (product.images || []).forEach((src) => sources.push(src));
    (product.media || []).forEach((media) => media && media.src && sources.push(media.src));
    if (product.featured_image) sources.push(product.featured_image);
    (product.variants || []).forEach((variant) => {
      if (variant.featured_image && variant.featured_image.src) sources.push(variant.featured_image.src);
    });
    return new Set(sources.map(imageKey).filter(Boolean));
  };

  const isSameProduct = (product, data) => {
    if (!product) return false;
    if (data.productId && String(product.id) === String(data.productId)) return true;
    if (data.imageKey && productImageKeys(product).has(data.imageKey)) return true;
    return Boolean(data.name) && normalize(product.title) === normalize(data.name);
  };

  const pickVariant = (product, data) => {
    const variants = product.variants || [];
    if (data.variantId) {
      const byId = variants.find((variant) => String(variant.id) === String(data.variantId));
      if (byId) return byId;
    }
    if (variants.length === 1) return variants[0];

    const label = normalize(data.variant);
    if (label) {
      const byLabel = variants.find(
        (variant) =>
          normalize(variant.title) === label ||
          (variant.options || []).some((option) => normalize(option) === label)
      );
      if (byLabel) return byLabel;
    }

    if (data.imageKey) {
      const byImage = variants.find(
        (variant) => variant.featured_image && imageKey(variant.featured_image.src) === data.imageKey
      );
      if (byImage) return byImage;
    }

    if (data.price != null) {
      const byPrice = variants.filter((variant) => toMajorUnits(variant.price) === data.price);
      if (byPrice.length === 1) return byPrice[0];
    }

    return null;
  };

  const findProduct = async (data) => {
    if (data.handle) {
      const product = await fetchProduct(data.handle);
      if (product) return product;
    }

    const tried = new Set();
    const tryHandles = async (handles) => {
      for (const handle of handles) {
        if (tried.has(handle) || tried.size >= MAX_CANDIDATES) continue;
        tried.add(handle);
        const product = await fetchProduct(handle);
        if (isSameProduct(product, data)) return product;
      }
      return null;
    };

    return (
      (await tryHandles(getRecentlyViewedHandles())) ||
      (await tryHandles(await searchHandles(data.name)))
    );
  };

  // item_id per the CRO spec ("Unique SKU ID"): the variant SKU, else the variant id.
  const skuOrId = (sku, id) => (sku && String(sku).trim()) || (id != null ? String(id) : null);

  // Resolves to { item_id, variant_id, item_name, item_category, product_id } — any of which may
  // be null when the lookup cannot place the product; never rejects.
  const resolutions = new Map();

  const resolveItem = (data) => {
    const key = contentKey(data);
    if (!resolutions.has(key)) {
      const fallback = {
        item_id:       data.variantId,
        variant_id:    data.variantId,
        item_name:     data.name,
        item_category: data.productType,
        product_id:    data.productId,
        compare_at:    null,
      };

      resolutions.set(
        key,
        findProduct(data)
          .then((product) => {
            if (!product) return fallback;
            const variant = pickVariant(product, data);
            return {
              item_id:       variant ? skuOrId(variant.sku, variant.id) : fallback.item_id,
              variant_id:    variant ? String(variant.id) : fallback.variant_id,
              item_name:     product.title || fallback.item_name,
              item_category: product.type || fallback.item_category,
              product_id:    String(product.id),
              compare_at:    variant && variant.compare_at_price ? toMajorUnits(variant.compare_at_price) : null,
            };
          })
          .catch(() => fallback)
          .then((resolved) => ({ ...resolved, settled: true }))
      );
    }
    return resolutions.get(key);
  };

  // Synchronous read of a lookup that has already finished (for clicks, which cannot wait).
  const settledResolutions = new Map();

  const prefetch = (data) => {
    const key = contentKey(data);
    resolveItem(data).then((resolved) => settledResolutions.set(key, resolved));
  };

  const withTimeout = (promise, ms) =>
    Promise.race([promise, new Promise((resolve) => setTimeout(() => resolve(null), ms))]);

  // ── Payloads ────────────────────────────────────────────────────────────────
  // The widget shows a single product, so index is always 1 (the spec's index starts from 1).
  const buildItem = (data, resolved) => ({
    item_id:        (resolved && resolved.item_id) || data.variantId || null,
    item_name:      (resolved && resolved.item_name) || data.name || null,
    index:          1,
    item_brand:     BRAND,
    item_category:  (resolved && resolved.item_category) || data.productType || 'NA',
    item_category2: 'NA',
    item_category3: 'NA',
    item_category4: 'NA',
    item_category5: 'NA',
    item_list_id:   LIST_ID,
    item_list_name: LIST_NAME,
    item_variant:   data.variant || null,
    price:          data.price,
  });

  const pushEcommerceEvent = (eventName, ecommerce, widget) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event:              eventName,
      client_id:          getClientId(),
      exp_variant_string: getExpVariantString(widget),
      item_list_id:       LIST_ID,
      item_list_name:     LIST_NAME,
      ecommerce,
    });
  };

  // ── view_item_list ──────────────────────────────────────────────────────────
  // The widget can be inserted as an empty shell and filled in afterwards, so the impression is
  // attempted both when it scrolls into view and whenever its content changes while in view —
  // and only counted once it actually shows a product.
  const impressedKeys   = new Set();
  const inViewWidgets   = new WeakSet();
  const observedWidgets = new WeakSet();

  const trackView = async (widget) => {
    if (!inViewWidgets.has(widget) || !widget.isConnected) return;

    const data = readWidget(widget);
    if (!data.name) return;

    // Per PRODUCT, not per variant: switching size/shade in the widget is not a new impression
    // (spec: the same product must not re-trigger on the same page).
    // Keyed on the name, which never changes while the widget is up — the productId read from
    // the widget's saved product disappears once the widget clears it after an add to cart.
    const key = normalize(data.name);
    if (impressedKeys.has(key)) return;
    impressedKeys.add(key);

    const resolved = await withTimeout(resolveItem(data), RESOLVE_TIMEOUT_MS);
    pushEcommerceEvent(EVENTS.view, { items: [buildItem(data, resolved)] }, widget);
  };

  const observeWidget = (widget) => {
    if (observedWidgets.has(widget)) return;
    observedWidgets.add(widget);

    const onContentChange = () => {
      const data = readWidget(widget);
      if (data.name) prefetch(data);
      trackView(widget);
    };

    onContentChange();
    new MutationObserver(onContentChange).observe(widget, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['src', 'data-variant-id', 'data-product-id', 'data-product-handle'],
    });

    if (typeof IntersectionObserver === 'undefined') {
      inViewWidgets.add(widget);
      trackView(widget);
      return;
    }

    new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            inViewWidgets.add(widget);
            trackView(widget);
          } else {
            inViewWidgets.delete(widget);
          }
        });
      },
      { threshold: 0.5 }
    ).observe(widget);
  };

  const scanForWidgets = () => document.querySelectorAll(WIDGET_SELECTOR).forEach(observeWidget);

  new MutationObserver(scanForWidgets).observe(document.body, { childList: true, subtree: true });
  scanForWidgets();

  // ── select_item + ADD TO CART click ─────────────────────────────────────────
  // Capture phase so this runs before the widget's own handler navigates away — which is also
  // why select_item cannot wait on the lookup and uses whatever has already been resolved.
  let pendingAddToCart = null;

  document.addEventListener(
    'click',
    (event) => {
      const widget = event.target.closest?.(WIDGET_SELECTOR);
      if (!widget) return;

      const data = readWidget(widget);
      if (!data.name) return;

      if (event.target.closest(CTA_SELECTOR)) {
        pendingAddToCart = {
          widget,
          data,
          resolution: resolveItem(data),
          expiresAt:  Date.now() + ATC_WINDOW_MS,
        };
        return;
      }

      // A product click = the image or the name, the only parts the widget opens the product
      // from. Size/shade pills and the price only change what is shown — not a product click.
      if (!event.target.closest(PRODUCT_CLICK_SELECTOR)) return;

      const resolved = settledResolutions.get(contentKey(data)) || null;
      pushEcommerceEvent(EVENTS.select, { items: [buildItem(data, resolved)] }, widget);
    },
    true
  );

  // ── add_to_cart ─────────────────────────────────────────────────────────────
  const responseLines = (json) => {
    if (!json) return [];
    if (Array.isArray(json.items)) return json.items;
    return json.id ? [json] : [];
  };

  // Shopify joins multiple options as "Orange / 50gm"; the spec wants "Orange | 50gm".
  const toPipeVariant = (title) =>
    title && title !== 'Default Title' ? title.split(' / ').join(' | ') : null;

  const lineImageKey = (line) =>
    imageKey((line.featured_image && line.featured_image.url) || line.image);

  // Only the widget's own product counts — anything else added in the same window (another
  // widget, a bundle) is not this list's add_to_cart.
  const isWidgetLine = (line, data, resolved) => {
    const variantId = String(line.variant_id || line.id);
    // Matched on the variant id — item_id is the SKU, which is not how cart lines are keyed.
    if (resolved && resolved.variant_id) return variantId === String(resolved.variant_id);
    if (resolved && resolved.product_id) return String(line.product_id) === String(resolved.product_id);
    if (data.imageKey && lineImageKey(line) === data.imageKey) return true;
    return Boolean(data.name) && normalize(line.product_title) === normalize(data.name);
  };

  const handleCartAddResponse = async (json) => {
    const pending = pendingAddToCart;
    if (!pending) return;
    if (Date.now() > pending.expiresAt) {
      if (pendingAddToCart === pending) pendingAddToCart = null;
      return;
    }

    const { widget, data } = pending;
    const resolved = await withTimeout(pending.resolution, RESOLVE_TIMEOUT_MS);
    const lines    = responseLines(json).filter((line) => isWidgetLine(line, data, resolved));
    // An unrelated add in the same window leaves the widget's click pending for its own request.
    if (!lines.length || pendingAddToCart !== pending) return;
    pendingAddToCart = null;

    const items = lines.map((line) => {
      // Saving per unit against the highest "was" price known: the compare-at (MRP) the widget
      // shows / the variant carries, or the pre-discount cart price — same as the pair popup.
      const finalPrice   = toMajorUnits(line.final_price != null ? line.final_price : line.price);
      const regularPrice = Math.max(
        toMajorUnits(line.original_price),
        toMajorUnits(line.price),
        data.comparePrice || 0,
        (resolved && resolved.compare_at) || 0,
        finalPrice
      );
      return {
        ...buildItem(
          { ...data, price: finalPrice, variant: toPipeVariant(line.variant_title) || data.variant },
          {
            item_id:       skuOrId(line.sku, line.variant_id || line.id),
            item_name:     line.product_title || (resolved && resolved.item_name),
            item_category: line.product_type || (resolved && resolved.item_category),
          }
        ),
        discount: String(Math.round((regularPrice - finalPrice) * 100) / 100),
        quantity: Number(line.quantity) || 1,
      };
    });

    const value = items.reduce((sum, item) => sum + (item.price || 0) * item.quantity, 0);

    pushEcommerceEvent(
      EVENTS.addToCart,
      { currency: getCurrency(), value: value.toFixed(2), items },
      widget
    );
  };

  const isCartAdd = (method, url) =>
    String(method || 'GET').toUpperCase() === 'POST' && CART_ADD_RE.test(String(url || ''));

  // Both transports are only observed, never altered: the original request runs untouched and
  // the response is read from a clone.
  const originalFetch = window.fetch;
  if (typeof originalFetch === 'function') {
    window.fetch = function (input, init) {
      const request = originalFetch.call(window, input, init);
      if (!pendingAddToCart) return request;

      let method, url;
      try {
        url    = typeof input === 'string' ? input : (input && input.url) || String(input);
        method = (init && init.method) || (input && input.method);
      } catch (_) {
        return request;
      }
      if (!isCartAdd(method, url)) return request;

      request
        .then((response) => (response.ok ? response.clone().json() : null))
        .then((json) => { if (json) handleCartAddResponse(json); })
        .catch(() => {});

      return request;
    };
  }

  const XHR = window.XMLHttpRequest;
  if (XHR && XHR.prototype) {
    const originalOpen = XHR.prototype.open;
    const originalSend = XHR.prototype.send;

    XHR.prototype.open = function (method, url) {
      try {
        this.dnkLvpIsCartAdd = isCartAdd(method, url);
      } catch (_) {
        this.dnkLvpIsCartAdd = false;
      }
      return originalOpen.apply(this, arguments);
    };

    XHR.prototype.send = function () {
      if (this.dnkLvpIsCartAdd && pendingAddToCart) {
        this.addEventListener('load', () => {
          if (this.status < 200 || this.status >= 300) return;
          try {
            const json = this.responseType === 'json' ? this.response : JSON.parse(this.responseText);
            if (json) handleCartAddResponse(json);
          } catch (_) {
            // Not a JSON cart response — nothing to report.
          }
        });
      }
      return originalSend.apply(this, arguments);
    };
  }
})();
